'use client';

import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import logTrailDetailsVisitationAnimationViewed from '@alltrails/amplitude/events/logTrailDetailsVisitationAnimationViewed';
import logTrailDetailsVisitationCommunityHeatmapTapped from '@alltrails/amplitude/events/logTrailDetailsVisitationCommunityHeatmapTapped';
import Typography from '@alltrails/core/components/Typography';
import useIsVisible from '@alltrails/core/hooks/useIsVisible';
import DynamicButton from '@alltrails/denali/components/DynamicButton';
import Heading from '@alltrails/denali/components/Heading';
import PaidFeatureBadge from '@alltrails/denali/components/SubscriptionBadge/PaidFeatureBadge';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import cardStyles from '../CardModule/CardModule.module.scss';
import styles from './Visitation.module.scss';

const DesktopAnimation = dynamic(() => import('./VisitationChartLottie'), { ssr: false });
const MobileAnimation = dynamic(() => import('./VisitationChartMobileLottie'), { ssr: false });
const TrailTrafficUpsellModal = dynamic(() => import('./TrailTrafficUpsellModal'), { ssr: false });

type Props = {
  isBot?: boolean;
  trailId: number;
};

export default function VisitationFree({ isBot, trailId }: Props) {
  const isMobile = useIsMobileSizedScreen();
  const [isUpsellOpen, setIsUpsellOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const hasLoggedAnimationViewEvent = useRef(false);
  const isVisible = useIsVisible(contentRef);

  useEffect(() => {
    if (isVisible && !isBot && !hasLoggedAnimationViewEvent.current) {
      logTrailDetailsVisitationAnimationViewed({ trail_id: trailId });
      hasLoggedAnimationViewEvent.current = true;
    }
  }, [isVisible, isBot, trailId]);

  return (
    <div ref={contentRef} className={classNames(cardStyles.card, styles.mobileWrapper)}>
      <div className={classNames(cardStyles.inner, styles.mobileInner)}>
        <div className={styles.header}>
          <Heading as="h2" size="300" className={styles.title}>
            <FormattedMessage defaultMessage="Popular times" />
          </Heading>
          <PaidFeatureBadge featureTier="plus" size="md" variant="full" />
        </div>
        <div className={styles.upsellContent}>
          {!isBot && (isMobile ? <MobileAnimation /> : <DesktopAnimation />)}
          <div className={styles.upsellText}>
            <Heading as="h3" size="300">
              <FormattedMessage defaultMessage="When is the trail busiest?" />
            </Heading>
            <Typography variant="text100" color="secondary">
              <FormattedMessage defaultMessage="Beat the crowds with weekly and monthly trail activity data from our community" />
            </Typography>
          </div>
        </div>
        <div className={styles.upsellButtonContainer}>
          <div className={styles.upsellAction}>
            <DynamicButton
              className={styles.communityHeatmapButton}
              imageAlt=""
              onClick={() => {
                logTrailDetailsVisitationCommunityHeatmapTapped({ trail_id: trailId });
                setIsUpsellOpen(true);
              }}
              src="https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-communityHeatmap@2x.png"
              size="md"
              text={
                <Typography variant="heading200">
                  <FormattedMessage defaultMessage="Community heatmap" />
                </Typography>
              }
            />
          </div>
        </div>
      </div>
      {isUpsellOpen && <TrailTrafficUpsellModal isOpen={isUpsellOpen} onCloseRequest={() => setIsUpsellOpen(false)} trailId={trailId} />}
    </div>
  );
}
